import type { Idea, Platform } from "@szyos/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { api, errorMessage } from "../api";
import { EmptyState, ErrorNote, useToast } from "../components/feedback";
import { dateTime } from "../format";
import { PauseChooser } from "../components/Focus";
import { MaterialPanel } from "../components/MaterialPanel";
import { Icon } from "../icons";
import { MAX_FOCUS_PROJECTS, isIdeaEditable } from "@szyos/domain";

const GROUPS: { key: Idea["maturity"]; label: string; hint: string }[] = [
  { key: "RAW", label: "Roh", hint: "frisch festgehalten" },
  { key: "CLARIFIED", label: "Geklärt", hint: "Ziel und Zielgruppe klar" },
  { key: "READY", label: "Bereit", hint: "kann Projekt werden" },
  { key: "PARKED", label: "Geparkt", hint: "später wieder ansehen" },
];

export const PLATFORMS: { value: Platform; label: string; profile: string }[] = [
  { value: "web_pwa", label: "Web / PWA", profile: "pwa-static" },
  { value: "android", label: "Android", profile: "android-kotlin" },
  { value: "backend", label: "Backend", profile: "node-service" },
  { value: "minecraft_mod", label: "Minecraft-Mod", profile: "minecraft-mod" },
  { value: "other", label: "Sonstiges", profile: "generic" },
];

/**
 * Idee zum Projekt machen. Wer es gleich in den Fokus nimmt, wählt bei vollem Fokus, welches Projekt pausiert
 * (UX-10): Neues kommt nur dazu, wenn dafür etwas anderes wartet.
 */
export function PromoteForm({ idea, onDone }: { idea: Idea; onDone: () => void }) {
  const [platform, setPlatform] = useState<Platform>("web_pwa");
  const [focus, setFocus] = useState(false);
  const [pause, setPause] = useState("");
  const navigate = useNavigate();
  const toast = useToast();
  const id = useId();
  const projects = useQuery({ queryKey: ["projects"], queryFn: api.projects });
  const focused = (projects.data ?? []).filter((p) => p.focus);
  const full = focused.length >= MAX_FOCUS_PROJECTS;
  const promote = useMutation({
    mutationFn: () =>
      api.promoteIdea(idea.id, {
        platform,
        profileKey: PLATFORMS.find((p) => p.value === platform)?.profile ?? "generic",
        focus,
        ...(focus && full && pause ? { pauseProjectId: pause } : {}),
      }),
    onSuccess: (project) => {
      toast(`Projekt „${project.title}“ angelegt`);
      onDone();
      void navigate({ to: "/projects/$projectId", params: { projectId: project.id } });
    },
    onError: (err) => toast(errorMessage(err), "error"),
  });
  return (
    <div className="promote">
      <label htmlFor={id}>Plattform</label>
      <select id={id} value={platform} onChange={(e) => setPlatform(e.target.value as Platform)}>
        {PLATFORMS.map((p) => (
          <option key={p.value} value={p.value}>
            {p.label} (Profil {p.profile})
          </option>
        ))}
      </select>
      <label className="check-row">
        <input type="checkbox" checked={focus} onChange={(e) => setFocus(e.target.checked)} /> Gleich in den
        Fokus nehmen
      </label>
      {focus && full && <PauseChooser focused={focused} value={pause} onChange={setPause} />}
      {!focus && (
        <p className="muted">
          Ohne Fokus wartet das Projekt geparkt. {focused.length} von {MAX_FOCUS_PROJECTS} Fokusplätzen
          belegt.
        </p>
      )}
      <button
        type="button"
        className="btn btn--primary"
        onClick={() => promote.mutate()}
        disabled={promote.isPending || (focus && full && !pause)}
      >
        <span className="btn-label">
          Zu Projekt machen <Icon name="arrow" />
        </span>
      </button>
    </div>
  );
}

const splitTags = (text: string): string[] =>
  text
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);

/** Titel, Text und Schlagworte einer gespeicherten Idee ändern. */
function IdeaEditForm({ idea, onDone }: { idea: Idea; onDone: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [title, setTitle] = useState(idea.title);
  const [body, setBody] = useState(idea.body);
  const [tags, setTags] = useState(idea.tags.join(", "));
  const [error, setError] = useState<string | null>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const titleId = useId();
  const bodyId = useId();
  const tagsId = useId();

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  const save = useMutation({
    mutationFn: () =>
      api.updateIdea(idea.id, { title, body, tags: splitTags(tags), expectedUpdatedAt: idea.updatedAt }),
    onSuccess: async () => {
      toast("Idee gespeichert");
      await queryClient.invalidateQueries({ queryKey: ["ideas"] });
      onDone();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError("Ein Titel genügt, aber ohne geht es nicht.");
      return;
    }
    save.mutate();
  };

  return (
    <form
      className="form idea-edit"
      onSubmit={submit}
      noValidate
      aria-label={`Idee „${idea.title}“ bearbeiten`}
    >
      <label htmlFor={titleId}>Titel</label>
      <input
        id={titleId}
        ref={titleRef}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={200}
      />
      <label htmlFor={bodyId}>Worum geht es?</label>
      <textarea
        id={bodyId}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={5}
        maxLength={10000}
      />
      <label htmlFor={tagsId}>Schlagworte, durch Komma getrennt</label>
      <input id={tagsId} value={tags} onChange={(e) => setTags(e.target.value)} />
      <ErrorNote error={error} />
      <div className="idea-actions">
        <button type="submit" className="chip-btn is-primary" disabled={save.isPending}>
          Speichern
        </button>
        <button type="button" className="chip-btn" onClick={onDone} disabled={save.isPending}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}

function IdeaCard({ idea }: { idea: Idea }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [promoting, setPromoting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showMaterial, setShowMaterial] = useState(false);
  const editable = isIdeaEditable(idea.maturity);
  const move = useMutation({
    mutationFn: (m: "RAW" | "CLARIFIED" | "READY" | "PARKED" | "DISCARDED") =>
      api.setIdeaMaturity(idea.id, m),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["ideas"] }),
    onError: (err) => toast(errorMessage(err), "error"),
  });
  const actions: {
    label: string;
    to: "RAW" | "CLARIFIED" | "READY" | "PARKED" | "DISCARDED";
    show: boolean;
  }[] = [
    { label: "Geklärt", to: "CLARIFIED", show: idea.maturity === "RAW" },
    { label: "Bereit", to: "READY", show: idea.maturity === "RAW" || idea.maturity === "CLARIFIED" },
    { label: "Parken", to: "PARKED", show: ["RAW", "CLARIFIED", "READY"].includes(idea.maturity) },
    {
      label: "Wiederherstellen",
      to: "RAW",
      show: idea.maturity === "PARKED" || idea.maturity === "DISCARDED",
    },
    { label: "Verwerfen", to: "DISCARDED", show: idea.maturity !== "DISCARDED" },
  ];
  return (
    <li className="idea-card">
      <div className="idea-head">
        <strong>{idea.title}</strong>
        <time className="muted mono" dateTime={idea.createdAt}>
          {dateTime(idea.createdAt)}
        </time>
      </div>
      {editing ? (
        <IdeaEditForm idea={idea} onDone={() => setEditing(false)} />
      ) : (
        <>
          {idea.body && <p className="idea-body">{idea.body}</p>}
          {idea.tags.length > 0 && (
            <ul className="tags">
              {idea.tags.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          )}
        </>
      )}
      <div className="idea-actions">
        {editable && !editing && (
          <button
            type="button"
            className="chip-btn"
            onClick={() => {
              setPromoting(false);
              setEditing(true);
            }}
            aria-label={`Idee „${idea.title}“ bearbeiten`}
          >
            <Icon name="edit" /> Bearbeiten
          </button>
        )}
        <button
          type="button"
          className="chip-btn"
          onClick={() => setShowMaterial((v) => !v)}
          aria-expanded={showMaterial}
          aria-label={`Material zu „${idea.title}“`}
        >
          <Icon name="file" /> Material
        </button>
        {actions
          .filter((a) => a.show)
          .map((a) => (
            <button
              key={a.to}
              type="button"
              className="chip-btn"
              onClick={() => move.mutate(a.to)}
              disabled={move.isPending}
            >
              {a.label}
            </button>
          ))}
        {idea.maturity !== "DISCARDED" && (
          <Link to="/ideas/$ideaId" params={{ ideaId: idea.id }} className="chip-btn is-primary">
            <Icon name="bot" /> Mit KI ausarbeiten
          </Link>
        )}
        {idea.maturity !== "DISCARDED" && (
          <button
            type="button"
            className="chip-btn is-primary"
            onClick={() => setPromoting((p) => !p)}
            aria-expanded={promoting}
          >
            <Icon name="folder" /> Zu Projekt machen
          </button>
        )}
      </div>
      {promoting && <PromoteForm idea={idea} onDone={() => setPromoting(false)} />}
      {showMaterial && <MaterialPanel owner={{ type: "ideas", id: idea.id }} editable={editable} />}
    </li>
  );
}

export function Ideas({ autoFocus }: { autoFocus: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const ideas = useQuery({ queryKey: ["ideas"], queryFn: api.ideas });
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [tags, setTags] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [showDiscarded, setShowDiscarded] = useState(false);
  const titleRef = useRef<HTMLInputElement>(null);
  const titleId = useId();
  const bodyId = useId();
  const tagsId = useId();

  useEffect(() => {
    if (autoFocus) titleRef.current?.focus();
  }, [autoFocus]);

  const capture = useMutation({
    mutationFn: () =>
      api.captureIdea({
        title,
        body,
        tags: splitTags(tags),
        origin: window.matchMedia("(max-width: 760px)").matches ? "mobile" : "web",
      }),
    onSuccess: async () => {
      setTitle("");
      setBody("");
      setTags("");
      setError(null);
      toast("Idee festgehalten");
      await queryClient.invalidateQueries({ queryKey: ["ideas"] });
      titleRef.current?.focus();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError("Ein Titel genügt, aber ohne geht es nicht.");
      return;
    }
    capture.mutate();
  };

  const list = ideas.data ?? [];
  return (
    <>
      <section className="hero">
        <div className="hero-text">
          <h1>
            Ideen <span className="glow-word">festhalten.</span>
          </h1>
          <p className="hero-sub">
            Titel genügt, Enter speichert. Reif gewordene Ideen werden mit einem Klick zum Projekt.
          </p>
        </div>
      </section>
      <section className="ideas-layout">
        <form className="panel form capture" onSubmit={submit} noValidate aria-labelledby="captureTitle">
          <h2 id="captureTitle">Neue Idee</h2>
          <label htmlFor={titleId}>Titel</label>
          <input
            id={titleId}
            ref={titleRef}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            placeholder="z. B. Cache-Aufräumer für alte Handys"
          />
          <label htmlFor={bodyId}>Worum geht es? (optional)</label>
          <textarea
            id={bodyId}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            maxLength={10000}
          />
          <label htmlFor={tagsId}>Schlagworte, durch Komma getrennt (optional)</label>
          <input
            id={tagsId}
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="android, schule"
          />
          <ErrorNote error={error} />
          <button type="submit" className="btn btn--primary" disabled={capture.isPending}>
            <span className="btn-label">
              <Icon name="plus" /> Festhalten
            </span>
          </button>
        </form>
        <div className="idea-board">
          {list.length === 0 && !ideas.isLoading && (
            <EmptyState
              icon="idea"
              title="Noch keine Ideen"
              text="Hier sammeln sich deine Ideen, bis sie reif für ein Projekt sind."
            />
          )}
          {GROUPS.map((g) => {
            const items = list.filter((i) => i.maturity === g.key);
            if (items.length === 0) return null;
            return (
              <section key={g.key} className="idea-group" aria-label={g.label}>
                <h2>
                  {g.label}{" "}
                  <span className="muted">
                    ({items.length}) · {g.hint}
                  </span>
                </h2>
                <ul>
                  {items.map((i) => (
                    <IdeaCard key={i.id} idea={i} />
                  ))}
                </ul>
              </section>
            );
          })}
          {list.some((i) => i.maturity === "DISCARDED" || i.maturity === "PROMOTED") && (
            <button
              type="button"
              className="link"
              onClick={() => setShowDiscarded((s) => !s)}
              aria-expanded={showDiscarded}
            >
              {showDiscarded ? "Erledigte ausblenden" : "Verworfene und umgesetzte anzeigen"}
            </button>
          )}
          {showDiscarded && (
            <ul className="idea-archive">
              {list
                .filter((i) => i.maturity === "DISCARDED" || i.maturity === "PROMOTED")
                .map((i) =>
                  i.maturity === "DISCARDED" ? (
                    <IdeaCard key={i.id} idea={i} />
                  ) : (
                    <li key={i.id} className="idea-card is-done">
                      <strong>{i.title}</strong> <span className="chip chip--ghost">Projekt</span>
                    </li>
                  ),
                )}
            </ul>
          )}
        </div>
      </section>
    </>
  );
}
